// src/SuperSessions.js
import React, { useEffect, useState } from 'react';
import API from './api';

function SuperSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgFilter, setOrgFilter] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [forceLogoutId, setForceLogoutId] = useState(null);
  const [forceReason, setForceReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (orgFilter) params.organization_id = orgFilter;
      if (search) params.search = search;

      const res = await API.get('/super/sessions', { params });

      if (res.data && res.data.success) {
        setSessions(res.data.sessions);
      } else {
        setError(res.data?.message || 'Error loading sessions');
      }
    } catch (err) {
      console.error('fetchSessions error', err);
      setError('Error loading sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleForceLogout = async () => {
    if (!forceLogoutId) return;
    setIsSubmitting(true);
    try {
      const res = await API.post(
        `/super/sessions/${forceLogoutId}/force-logout`,
        { reason: forceReason }
      );

      if (res.data && res.data.success) {
        await fetchSessions();
        setForceLogoutId(null);
        setForceReason('');
      } else {
        alert(res.data?.message || 'Error revoking session');
      }
    } catch (err) {
      console.error('forceLogout error', err);
      alert('Error revoking session');
    } finally {
      setIsSubmitting(false);
    }
  };

  const shortUserAgent = (ua) => {
    if (!ua) return '';
    if (ua.length <= 60) return ua;
    return ua.slice(0, 60) + '...';
  };

  const shortIp = (ip) => ip || '';

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchSessions();
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">
        Active sessions (Super Admin)
      </h1>

      <form
        onSubmit={handleFilterSubmit}
        className="mb-4 flex flex-wrap gap-2 items-end"
      >
        <div>
          <label className="block text-sm font-medium mb-1">
            Organization ID
          </label>
          <input
            type="text"
            className="border rounded px-2 py-1 text-sm"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            placeholder="All"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Search user (name/email)
          </label>
          <input
            type="text"
            className="border rounded px-2 py-1 text-sm w-64"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Jane, john@example.com..."
          />
        </div>
        <button
          type="submit"
          className="bg-blue-600 text-white text-sm px-3 py-2 rounded hover:bg-blue-700"
        >
          Filter
        </button>
        <button
          type="button"
          onClick={() => {
            setOrgFilter('');
            setSearch('');
            fetchSessions();
          }}
          className="ml-2 text-sm px-3 py-2 rounded border border-gray-300 hover:bg-gray-100"
        >
          Reset
        </button>
      </form>

      {error && (
        <div className="mb-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {loading ? (
        <div>Loading sessions...</div>
      ) : sessions.length === 0 ? (
        <div>No active sessions found.</div>
      ) : (
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 py-1 text-left">User</th>
                <th className="border px-2 py-1 text-left">Role</th>
                <th className="border px-2 py-1 text-left">Org</th>
                <th className="border px-2 py-1 text-left">IP</th>
                <th className="border px-2 py-1 text-left">Device</th>
                <th className="border px-2 py-1 text-left">Started</th>
                <th className="border px-2 py-1 text-left">Last seen</th>
                <th className="border px-2 py-1 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1">
                    <div className="font-semibold">
                      {s.user_name || s.user_email}
                    </div>
                    <div className="text-xs text-gray-500">
                      {s.user_email}
                    </div>
                  </td>
                  <td className="border px-2 py-1">
                    <span className="inline-block px-2 py-0.5 rounded bg-gray-200 text-xs">
                      {s.user_role}
                    </span>
                  </td>
                  <td className="border px-2 py-1">
                    <div className="font-semibold">
                      {s.organization_name || '—'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Org ID: {s.organization_id ?? '—'}
                    </div>
                  </td>
                  <td className="border px-2 py-1">
                    <span className="text-xs">{shortIp(s.ip_address)}</span>
                  </td>
                  <td className="border px-2 py-1">
                    <span className="text-xs" title={s.user_agent}>
                      {shortUserAgent(s.user_agent)}
                    </span>
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    {s.created_at}
                  </td>
                  <td className="border px-2 py-1 text-xs">
                    {s.last_seen_at}
                  </td>
                  <td className="border px-2 py-1 text-center">
                    <button
                      className="text-xs px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                      onClick={() => {
                        setForceLogoutId(s.id);
                        setForceReason('');
                      }}
                    >
                      Force logout
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal za potvrdu force logout-a */}
      {forceLogoutId && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-4 max-w-md w-full">
            <h2 className="text-lg font-bold mb-2">Force logout session</h2>
            <p className="text-sm mb-3">
              Are you sure you want to force logout this session?
              The user will be logged out on their next request.
            </p>
            <label className="block text-sm font-medium mb-1">
              Reason (optional)
            </label>
            <textarea
              className="border rounded w-full p-2 text-sm mb-3"
              rows={3}
              value={forceReason}
              onChange={(e) => setForceReason(e.target.value)}
              placeholder="Suspicious activity, user terminated, security reason..."
            />

            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 text-sm border rounded"
                onClick={() => {
                  setForceLogoutId(null);
                  setForceReason('');
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                onClick={handleForceLogout}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Working...' : 'Force logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperSessions;

